/**
 * Action-related types — actions, exceptions, validation, approval logs,
 * revision requests, and action request/response shapes.
 *
 * Aligned with: v2-invoice-api-contract.md (commit 0607bbe)
 */
import type { LifecycleStatus, Role } from "./common";

// --- Actions ---

export const ACTION = {
  APPROVE: "APPROVE",
  REJECT: "REJECT",
  RESOLVE_EXCEPTION: "RESOLVE_EXCEPTION",
  OVERRIDE_EXCEPTION: "OVERRIDE_EXCEPTION",
  SELECT_GRN: "SELECT_GRN",
  MANUAL_ENTRY: "MANUAL_ENTRY",
  REQUEST_REVISION: "REQUEST_REVISION",
  EDIT: "EDIT",
  // Shows the "Reprocess invoice" button; runs via POST /v2/vip/invoices/reprocess,
  // never the /action endpoint.
  REPROCESS: "REPROCESS",
} as const;

export type Action = (typeof ACTION)[keyof typeof ACTION];

// --- Exception types ---

export const EXCEPTION_TYPE = {
  MISSING_GRN: "MISSING_GRN",
  MULTI_GRN: "MULTI_GRN",
  QTY_VARIANCE: "QTY_VARIANCE",
  AMOUNT_MISMATCH: "AMOUNT_MISMATCH",
  PARSING_FAILURE: "PARSING_FAILURE",
  DUPLICATE: "DUPLICATE",
  HOLD_VENDOR: "HOLD_VENDOR",
} as const;

export type ExceptionType = (typeof EXCEPTION_TYPE)[keyof typeof EXCEPTION_TYPE];

// --- Match exception ---

export type MatchException = {
  id: number;
  invoice_id: number;
  exception_type: ExceptionType;
  field_name: string;
  expected_value: string;
  actual_value: string;
  severity: string;
  resolved: boolean;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_type: string | null;
  created_at: string;
};

// --- Validation (from GET /v2/vip/invoice/:id/validation) ---

export type ParsedField = {
  text: string;
  confidence: number;
};

export type ParseResult = {
  parser_engine_type: string;
  extracted_entity_data: Record<string, ParsedField>;
};

export type ValidationFieldResult = {
  po_value?: string | number | null;
  invoice_value?: string | number | null;
  parser_value?: string | number | null;
  match_status: boolean;
  confidence?: number | null;
};

export type ValidationMatchResult = {
  match_status: boolean;
  match_percentage: number;
  expected_match_percentage?: number;
  validation_date?: string;
  validation_field_results?: Record<string, ValidationFieldResult>;
};

/** The 3-way match results comparing parser output, invoice, and PO data. */
export type ValidationResult = {
  parser_invoice_po_result: ValidationMatchResult;
  parser_invoice_result: ValidationMatchResult;
  invoice_po_result: ValidationMatchResult;
};

export type InvoiceValidationResponse = {
  invoice_id: number;
  parse_result: ParseResult | null;
  validation_result: ValidationResult | null;
};

// --- Validation checks (from detail response with include=checks) ---

/** A single field-level validation check from the v2 checks array. */
export type ValidationCheck = {
  field: string;
  match: boolean;
  reason: string;
  /** A list for multi-value checks (e.g. `hsn_code`: one entry per line item / PO code). */
  system_value: CheckValue;
  invoice_value: CheckValue;
};

export type CheckValue = string | string[] | null;

// --- Validation logs (from GET/POST /v2/vip/invoice/:id/validation-logs) ---

export const VALIDATION_STEP = {
  DUPLICATE_CHECK: "DUPLICATE_CHECK",
  PO_MATCH: "PO_MATCH",
  AMOUNT_MATCH: "AMOUNT_MATCH",
  DATE_VALIDATION: "DATE_VALIDATION",
  GRN_MATCH: "GRN_MATCH",
  QTY_MATCH: "QTY_MATCH",
  ADDRESS_MATCH: "ADDRESS_MATCH",
  HSN_MATCH: "HSN_MATCH",
  VENDOR_CHECK: "VENDOR_CHECK",
  OCR_EXTRACTION: "OCR_EXTRACTION",
  THREE_WAY_MATCH: "THREE_WAY_MATCH",
  OVERALL_DECISION: "OVERALL_DECISION",
} as const;

export type ValidationStep = (typeof VALIDATION_STEP)[keyof typeof VALIDATION_STEP];

export type ValidationLogEntry = {
  id: number;
  invoice_id: number;
  validation_step: ValidationStep;
  status: "PASS" | "FAIL" | "WARN" | "SKIPPED";
  field_name?: string;
  expected_value?: string;
  actual_value?: string;
  confidence?: number;
  reason: string;
  agent_model?: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export type ValidationLogSummary = {
  total_checks: number;
  passed: number;
  failed: number;
  warnings: number;
  skipped: number;
};

export type ValidationLogsResponse = {
  invoice_id: number;
  logs: ValidationLogEntry[];
  summary: ValidationLogSummary;
};

export type CreateValidationLogEntry = Omit<ValidationLogEntry, "id" | "invoice_id" | "created_at">;

export type CreateValidationLogsResponse = {
  invoice_id: number;
  log_ids: number[];
  message: string;
};

// --- Approval log (from detail response) ---

export type ApprovalLog = {
  id: number;
  invoice_id: number;
  approver_id: string;
  approval_action: string;
  comments: string;
  amount_at_approval: number;
  created_at: string;
};

// --- Revision request ---

export type RevisionRequest = {
  id: number;
  invoice_id: number;
  requested_by: string;
  reason: string;
  fields_to_revise: string[];
  status: "PENDING" | "REVISED" | "COMPLETED" | "CANCELLED";
  revised_invoice_id?: number;
  vendor_response_date?: string;
  created_at: string;
  completed_at?: string;
};

// --- Audit trail (from GET /v2/vip/invoice/:id/audit) ---

export type AuditTrailEntry = {
  timestamp: string;
  event_type: string;
  actor: string;
  details: string;
};

export type AuditTrailResponse = {
  invoice_id: number;
  audit_trail: AuditTrailEntry[];
};

// --- Exception queue (from GET /v2/vip/invoice/exceptions) ---

export type ExceptionQueueItem = {
  exception_id: number;
  invoice_id: number;
  invoice_number: string;
  po_code: string;
  vendor_name: string;
  invoice_value: number;
  exception_type: ExceptionType;
  exception_reason: string;
  created_at: string;
  resolved_at: string | null;
  sla_deadline: string;
  is_overdue: boolean;
};

export type ExceptionQueueParams = {
  exception_type?: string;
  status?: "OPEN" | "RESOLVED";
  po_code?: string;
  limit?: number;
  offset?: number;
};

export type ExceptionQueueResponse = {
  exceptions: ExceptionQueueItem[];
  exception_counts: Record<string, number>;
  total_count: number;
  limit: number;
  offset: number;
};

// --- Revision request payload ---

export type RevisionRequestPayload = {
  revision_reason: string;
  required_fields?: string[];
  comments?: string;
};

// --- Action request/response ---

export type InvoiceActionRequest = {
  action: Action;
  lifecycle_version: number;
  current_status?: LifecycleStatus;
  comments?: string;
  exception_ids?: number[];
  override_reason?: string;
  selected_grn_ids?: number[];
  ocr_fields?: Record<string, unknown>;
  revision_reason?: string;
  required_fields?: string[];
};

export type InvoiceActionResponse = {
  invoice_id: number;
  previous_status: LifecycleStatus;
  current_status: LifecycleStatus;
  lifecycle_version: number;
  action_performed: Action;
  message: string;
};

// --- Error response (standard shape across all endpoints) ---

export type InvoiceApiError = {
  error: string;
  current_status?: LifecycleStatus;
  allowed_actions?: Action[];
  current_version?: number;
  required_role?: Role;
  invoice_amount?: number;
};
